import React, { useState } from 'react';
import { Patient, Medication } from '../types/patient';
import { Pill, Calendar, AlertTriangle, CheckCircle, Plus, Search, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import Modal from './Modal';
import { getBadgeClasses } from '../utils/styleHelpers';

interface MedicationsProps {
  patient: Patient;
  onMedicationAdded: (patientId: string, medication: Medication) => void;
}

interface NewMedication {
  name: string;
  dosage: string;
  frequency: string;
  duration: string;
  startDate: string;
  endDate: string;
  pharmacy: string;
}

export default function Medications({ patient, onMedicationAdded }: MedicationsProps) {
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'discontinued' | 'delivered' | 'mixed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMedicationId, setExpandedMedicationId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<NewMedication>({
    name: '',
    dosage: '',
    frequency: '',
    duration: '',
    startDate: '',
    endDate: '',
    pharmacy: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof NewMedication, string>>>({});

  const filteredMedications = patient.medications.filter(med => {
    // Filter by status
    if (filterStatus !== 'all' && med.overallStatus !== filterStatus) {
      return false;
    }
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        med.name.toLowerCase().includes(query) ||
        med.dosage.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-success-600 bg-success-100';
      case 'discontinued': return 'text-gray-600 bg-gray-100';
      case 'delivered': return 'text-primary-600 bg-primary-100';
      case 'mixed': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'discontinued': return <AlertTriangle className="w-4 h-4" />;
      case 'delivered': return <Calendar className="w-4 h-4" />;
      case 'mixed': return <Pill className="w-4 h-4" />;
      default: return <Pill className="w-4 h-4" />;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (formErrors[name as keyof NewMedication]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof NewMedication, string>> = {};

    if (!formData.name.trim()) errors.name = 'Medication name is required';
    if (!formData.dosage.trim()) errors.dosage = 'Dosage is required';
    if (!formData.frequency.trim()) errors.frequency = 'Frequency is required';
    if (!formData.startDate) errors.startDate = 'Start date is required';
    if (!formData.pharmacy.trim()) errors.pharmacy = 'Pharmacy is required';

    if (formData.endDate && new Date(formData.endDate) < new Date(formData.startDate)) {
      errors.endDate = 'End date must be after start date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Create the new medication object
    const newMedication: Medication = {
      id: `med-${Date.now()}`,
      name: formData.name,
      dosage: formData.dosage,
      code: 'UNKNOWN',  // User-added medications don't have RxNorm code
      datePairs: [{
        prescribedDate: formData.startDate,
        status: 'active'
      }],
      overallStatus: 'active',
      totalPrescriptions: 1,
      totalDeliveries: 0
    };

    // Add the medication to the patient
    onMedicationAdded(patient.id, newMedication);

    // Reset form and close modal
    setFormData({
      name: '',
      dosage: '',
      frequency: '',
      duration: '',
      startDate: '',
      endDate: '',
      pharmacy: '',
    });
    setFormErrors({});
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* Header and Filters */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-2">
          <Pill className="w-5 h-5 text-brand-dark-500" />
          <h2 className="text-xl font-bold text-brand-dark-600">Medications</h2>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={() => setIsModalOpen(true)}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Medication</span>
          </button>

          <div className="flex space-x-1">
            {(['all', 'active', 'discontinued', 'delivered', 'mixed'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  filterStatus === status
                    ? 'bg-brand-dark-500 text-white'
                    : 'bg-brand-beige-200 text-brand-dark-600 hover:bg-brand-beige-300'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-brand-taupe-500" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search medications by name or dosage..."
          className="block w-full pl-10 pr-3 py-2 border border-brand-taupe-300 rounded-lg leading-5 bg-white placeholder-brand-taupe-400 focus:outline-none focus:placeholder-brand-taupe-500 focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 sm:text-sm"
          aria-label="Search medications"
        />
      </div>

      {/* Add Medication Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setFormErrors({});
        }}
        title="Add New Medication"
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Medication Name */}
            <div className="md:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-brand-dark-600 mb-1">
                Medication Name <span className="text-danger-600">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 outline-none ${
                  formErrors.name ? 'border-danger-500' : 'border-brand-taupe-300'
                }`}
                placeholder="e.g., Lisinopril"
              />
              {formErrors.name && <p className="text-danger-600 text-sm mt-1">{formErrors.name}</p>}
            </div>

            {/* Dosage */}
            <div>
              <label htmlFor="dosage" className="block text-sm font-medium text-brand-dark-600 mb-1">
                Dosage <span className="text-danger-600">*</span>
              </label>
              <input
                type="text"
                id="dosage"
                name="dosage"
                value={formData.dosage}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 outline-none ${
                  formErrors.dosage ? 'border-danger-500' : 'border-brand-taupe-300'
                }`}
                placeholder="e.g., 10mg"
              />
              {formErrors.dosage && <p className="text-danger-600 text-sm mt-1">{formErrors.dosage}</p>}
            </div>

            {/* Frequency */}
            <div>
              <label htmlFor="frequency" className="block text-sm font-medium text-brand-dark-600 mb-1">
                Frequency <span className="text-danger-600">*</span>
              </label>
              <input
                type="text"
                id="frequency"
                name="frequency"
                value={formData.frequency}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 outline-none ${
                  formErrors.frequency ? 'border-danger-500' : 'border-brand-taupe-300'
                }`}
                placeholder="e.g., Once daily"
              />
              {formErrors.frequency && <p className="text-danger-600 text-sm mt-1">{formErrors.frequency}</p>}
            </div>

            {/* Duration */}
            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-brand-dark-600 mb-1">
                Duration
              </label>
              <input
                type="text"
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-brand-taupe-300 rounded-lg focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 outline-none"
                placeholder="e.g., 30 days"
              />
            </div>

            {/* Start Date */}
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-brand-dark-600 mb-1">
                Start Date <span className="text-danger-600">*</span>
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 outline-none ${
                  formErrors.startDate ? 'border-danger-500' : 'border-brand-taupe-300'
                }`}
              />
              {formErrors.startDate && <p className="text-danger-600 text-sm mt-1">{formErrors.startDate}</p>}
            </div>

            {/* End Date */}
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-brand-dark-600 mb-1">
                End Date (Optional)
              </label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 outline-none ${
                  formErrors.endDate ? 'border-danger-500' : 'border-brand-taupe-300'
                }`}
              />
              {formErrors.endDate && <p className="text-danger-600 text-sm mt-1">{formErrors.endDate}</p>}
            </div>

            {/* Pharmacy */}
            <div className="md:col-span-2">
              <label htmlFor="pharmacy" className="block text-sm font-medium text-brand-dark-600 mb-1">
                Pharmacy <span className="text-danger-600">*</span>
              </label>
              <input
                type="text"
                id="pharmacy"
                name="pharmacy"
                value={formData.pharmacy}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-dark-400 focus:border-brand-dark-500 outline-none ${
                  formErrors.pharmacy ? 'border-danger-500' : 'border-brand-taupe-300'
                }`}
                placeholder="e.g., CVS Pharmacy, 123 Main St"
              />
              {formErrors.pharmacy && <p className="text-danger-600 text-sm mt-1">{formErrors.pharmacy}</p>}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-brand-taupe-200">
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false);
                setFormErrors({});
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Add Medication
            </button>
          </div>
        </form>
      </Modal>

      {/* Medications List */}
      {filteredMedications.length === 0 ? (
        <div className="card text-center py-12">
          <Pill className="w-12 h-12 text-brand-taupe-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-brand-dark-600 mb-2">No medications found</h3>
          <p className="text-brand-taupe-600">No medications match the current filter criteria.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredMedications.map((medication) => {
            const isExpanded = expandedMedicationId === medication.id;

            return (
              <div key={medication.id} className="card hover:shadow-md transition-shadow">
                {/* Collapsed View - Always Visible */}
                <div
                  className="cursor-pointer"
                  onClick={() => setExpandedMedicationId(isExpanded ? null : medication.id)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Pill className="w-5 h-5 text-brand-dark-500" />
                        <h3 className="text-lg font-semibold text-brand-dark-600">
                          {medication.name}
                        </h3>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(medication.overallStatus)}`}>
                        {getStatusIcon(medication.overallStatus)}
                        {medication.overallStatus.charAt(0).toUpperCase() + medication.overallStatus.slice(1)}
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-brand-taupe-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-brand-taupe-500" />
                      )}
                    </div>
                  </div>

                  {/* Summary Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-brand-taupe-600">Dosage:</span>
                      <span className="ml-2 font-medium text-brand-dark-600">{medication.dosage}</span>
                    </div>
                    <div>
                      <span className="text-brand-taupe-600">Prescriptions:</span>
                      <span className="ml-2 font-medium text-brand-dark-600">{medication.totalPrescriptions}</span>
                    </div>
                    <div>
                      <span className="text-brand-taupe-600">Deliveries:</span>
                      <span className="ml-2 font-medium text-brand-dark-600">{medication.totalDeliveries}</span>
                    </div>
                  </div>
                </div>

                {/* Expanded View - Date Pairs Table */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-brand-taupe-200">
                    <h4 className="text-sm font-semibold text-brand-dark-600 mb-3">Prescription History</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-brand-taupe-300">
                            <th className="text-left py-2 px-3 text-brand-taupe-600 font-medium">Prescribed</th>
                            <th className="text-left py-2 px-3 text-brand-taupe-600 font-medium">Discontinued</th>
                            <th className="text-left py-2 px-3 text-brand-taupe-600 font-medium">Delivered</th>
                            <th className="text-left py-2 px-3 text-brand-taupe-600 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {medication.datePairs.map((pair, idx) => {
                            const formatSafeDate = (dateStr: string | undefined) => {
                              if (!dateStr) return '—';
                              try {
                                const date = new Date(dateStr);
                                // Check if date is valid
                                if (isNaN(date.getTime())) return '—';
                                return format(date, 'MMM dd, yyyy');
                              } catch (error) {
                                return '—';
                              }
                            };

                            return (
                              <tr key={idx} className="border-b border-brand-taupe-200 hover:bg-brand-beige-100">
                                <td className="py-2 px-3 text-brand-dark-600">
                                  {formatSafeDate(pair.prescribedDate)}
                                </td>
                                <td className="py-2 px-3 text-brand-dark-600">
                                  {formatSafeDate(pair.discontinuedDate)}
                                </td>
                                <td className="py-2 px-3 text-brand-dark-600">
                                  {formatSafeDate(pair.deliveredDate)}
                                </td>
                                <td className="py-2 px-3">
                                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(pair.status)}`}>
                                    {getStatusIcon(pair.status)}
                                    {pair.status.charAt(0).toUpperCase() + pair.status.slice(1)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Procedures Section */}
      {patient.procedures && patient.procedures.length > 0 && (
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <Activity className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-brand-dark-600">Medical Procedures</h3>
          </div>

          <div className="space-y-2">
            {patient.procedures
              .sort((a, b) => {
                // Sort by date, most recent first
                // Handle "Historical" dates
                if (a.date === 'Historical' && b.date === 'Historical') return 0;
                if (a.date === 'Historical') return 1;
                if (b.date === 'Historical') return -1;
                return new Date(b.date).getTime() - new Date(a.date).getTime();
              })
              .map((procedure, index) => {
                const formatProcedureDate = (dateStr: string) => {
                  if (!dateStr || dateStr === 'Historical' || dateStr.includes('Prior to')) {
                    return dateStr;
                  }
                  try {
                    const date = new Date(dateStr);
                    if (isNaN(date.getTime())) return dateStr;
                    return format(date, 'MMM dd, yyyy');
                  } catch (error) {
                    return dateStr;
                  }
                };

                return (
                  <div key={index} className="flex items-start justify-between p-3 bg-brand-beige-50 rounded-lg border border-brand-taupe-200 hover:bg-brand-beige-100 transition-colors">
                    <div className="flex items-start space-x-3 flex-1">
                      <Calendar className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="font-medium text-brand-dark-600">{procedure.procedure}</div>
                        <div className="text-sm text-brand-taupe-600 mt-0.5">
                          {formatProcedureDate(procedure.date)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Allergies Section */}
      {patient.allergies.length > 0 && (
        <div className="card border-2 border-danger-200">
          <div className="flex items-center space-x-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-danger-600" />
            <h3 className="text-lg font-semibold text-danger-900">Known Allergies</h3>
          </div>

          <div className="space-y-3">
            {patient.allergies.map((allergy) => (
              <div key={allergy.id} className="flex items-center justify-between p-3 bg-danger-50 rounded-lg border border-danger-200">
                <div>
                  <div className="font-medium text-brand-dark-600">{allergy.allergen}</div>
                  <div className="text-sm text-brand-taupe-600">{allergy.reaction}</div>
                </div>
                <div className={getBadgeClasses(
                  allergy.severity === 'severe' ? 'high' :
                  allergy.severity === 'moderate' ? 'medium' : 'low',
                  'sm'
                )}>
                  {allergy.severity.charAt(0).toUpperCase() + allergy.severity.slice(1)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card text-center bg-gradient-to-br from-success-50 to-success-100">
          <div className="text-3xl font-bold text-success-700">
            {patient.medications.filter(m => m.overallStatus === 'active').length}
          </div>
          <div className="text-sm text-success-900 font-medium mt-1">Active Medications</div>
        </div>

        <div className="card text-center bg-gradient-to-br from-brand-beige-100 to-brand-taupe-100">
          <div className="text-3xl font-bold text-brand-dark-600">
            {patient.medications.filter(m => m.overallStatus === 'discontinued').length}
          </div>
          <div className="text-sm text-brand-dark-500 font-medium mt-1">Discontinued</div>
        </div>

        <div className="card text-center bg-gradient-to-br from-primary-50 to-primary-100">
          <div className="text-3xl font-bold text-primary-700">
            {patient.medications.filter(m => m.overallStatus === 'delivered').length}
          </div>
          <div className="text-sm text-primary-900 font-medium mt-1">One-Time Delivered</div>
        </div>

        <div className="card text-center bg-gradient-to-br from-danger-50 to-danger-100">
          <div className="text-3xl font-bold text-danger-700">
            {patient.allergies.length}
          </div>
          <div className="text-sm text-danger-900 font-medium mt-1">Known Allergies</div>
        </div>
      </div>
    </div>
  );
}
